import { useState } from 'react';
import { API_BASE } from '../../utils/config';

const apiCall = async (url, options = {}) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const tenant = (parts.length >= 3) ? parts[0] : (parts.length === 2 && parts[1] === 'localhost') ? parts[0] : '';
  const headers = {
    'Content-Type': 'application/json',
    'x-user-id': user.id || '',
    'x-user-role': user.user_type || user.role || 'MANAGER',
    'X-Tenant-ID': tenant,
    ...options.headers
  };
  const baseUrl = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  const res = await fetch(`${baseUrl}${url}`, { ...options, headers });
  if (!res.ok) throw new Error(`HTTP error ${res.status}`);
  return res.json();
};

export default function ManagerExits({ employees = [], onSuccess }) {
  const [showTriggerForm, setShowTriggerForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [noticePeriod, setNoticePeriod] = useState('');
  const [lastWorkingDay, setLastWorkingDay] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNoticePeriodChange = (val) => {
    setNoticePeriod(val);
    if (val && val !== 'custom') {
      const days = parseInt(val, 10);
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + days);
      const year = targetDate.getFullYear();
      const month = String(targetDate.getMonth() + 1).padStart(2, '0');
      const day = String(targetDate.getDate()).padStart(2, '0');
      setLastWorkingDay(`${year}-${month}-${day}`);
    } else if (val === 'custom') {
      setLastWorkingDay('');
    }
  };

  const handleDateChange = (val) => {
    setLastWorkingDay(val);
    if (val) {
      setNoticePeriod('custom');
    }
  };

  // Filter out employees who are already in EXIT_PENDING or EXIT_APPROVED state
  const safeEmployees = employees || [];
  const activeEligibleEmployees = safeEmployees.filter(emp => emp.exit_status === 'ACTIVE');
  const exitingEmployees = safeEmployees.filter(emp => emp.exit_status !== 'ACTIVE');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser || !lastWorkingDay) {
      alert('Please select an employee and set their last working day.');
      return;
    }

    try {
      setIsSubmitting(true);
      await apiCall('/api/employees/exits/trigger', {
        method: 'POST',
        body: JSON.stringify({
          employeeId: selectedUser,
          lastWorkingDay
        })
      });
      // Reset form states
      setSelectedUser('');
      setLastWorkingDay('');
      setNoticePeriod('');
      setShowTriggerForm(false);
      if (onSuccess) onSuccess(); // Refresh parent dashboard config
    } catch (err) {
      console.error('Failed to trigger exit:', err);
      alert('Failed to register employee exit.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getExitBadgeStyle = (status, isOffboarded) => {
    if (isOffboarded) return styles.badgeOffboarded;
    if (status === 'EXIT_APPROVED') return styles.badgeApproved;
    return styles.badgePending;
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Project Exit Management</h2>
          <p style={styles.subtitle}>Register exiting team members and schedule their last working days.</p>
        </div>
        {!showTriggerForm && activeEligibleEmployees.length > 0 && (
          <button style={styles.btnTrigger} onClick={() => setShowTriggerForm(true)}>
            Initiate Exit
          </button>
        )}
      </div>

      {!safeEmployees.length && (
        <div style={{
          marginBottom: '20px',
          padding: '14px 18px',
          backgroundColor: 'rgba(79, 70, 229, 0.06)',
          border: '1px solid rgba(79, 70, 229, 0.15)',
          borderRadius: '12px',
          color: 'var(--text-main, #0f172a)',
          fontSize: '13px'
        }}>
          💡 <strong>Notice:</strong> No team members are currently assigned to your project. Exit clearances can be managed here once your team roster is onboarded.
        </div>
      )}

      {showTriggerForm ? (
        <div style={styles.formCard}>
          <div style={styles.formHeader}>
            <h3 style={styles.formTitle}>Initiate Exit Process</h3>
            <button style={styles.btnCancel} onClick={() => { setShowTriggerForm(false); setNoticePeriod(''); setSelectedUser(''); setLastWorkingDay(''); }}>Cancel</button>
          </div>

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Select Employee *</label>
                <select 
                  value={selectedUser} 
                  onChange={e => setSelectedUser(e.target.value)}
                  style={styles.select}
                  required
                >
                  <option value="">-- Choose Employee --</option>
                  {activeEligibleEmployees.map(emp => (
                    <option key={emp.user_id} value={emp.user_id}>
                      {emp.full_name} ({emp.email})
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Notice Period *</label>
                <select 
                  value={noticePeriod} 
                  onChange={e => handleNoticePeriodChange(e.target.value)}
                  style={styles.select}
                  required
                >
                  <option value="">-- Select Notice Period --</option>
                  <option value="15">15 Days</option>
                  <option value="30">30 Days</option>
                  <option value="60">60 Days</option>
                  <option value="90">90 Days</option>
                  <option value="custom">Custom (Select Date)</option>
                </select>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Last Working Day (LWD) *</label>
                <input 
                  type="date" 
                  value={lastWorkingDay} 
                  onChange={e => handleDateChange(e.target.value)}
                  style={styles.input}
                  required
                />
              </div>
            </div>

            <div style={styles.formFooter}>
              <span style={styles.infoText}>⚠️ Initiating an exit will label the employee as <b>EXIT_PENDING</b> until approved by the BU Head.</span>
              <button type="submit" disabled={isSubmitting} style={styles.btnSave}>
                {isSubmitting ? 'Submitting...' : 'Register Exit'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div style={styles.tableCard}>
          <h3 style={styles.tableTitle}>Exiting Team Members Log</h3>
          {exitingEmployees.length === 0 ? (
            <div style={styles.empty}>No exit registrations logged for this project.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr style={styles.trHeader}>
                  <th style={styles.th}>Employee Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Joining Date</th>
                  <th style={styles.th}>Last Working Day</th>
                  <th style={styles.th}>Exit Status</th>
                </tr>
              </thead>
              <tbody>
                {exitingEmployees.map(emp => (
                  <tr key={emp.id} style={styles.tr}>
                    <td style={styles.tdTitle}>{emp.full_name}</td>
                    <td style={styles.td}>{emp.email}</td>
                    <td style={styles.td}>{new Date(emp.joining_date).toLocaleDateString()}</td>
                    <td style={styles.td}>
                      {emp.last_working_day ? new Date(emp.last_working_day).toLocaleDateString() : 'N/A'}
                    </td>
                    <td style={styles.td}>
                      <span style={getExitBadgeStyle(emp.exit_status, emp.user_deleted_at)}>
                        {emp.user_deleted_at ? 'Offboarded' :
                         emp.exit_status === 'EXIT_APPROVED' ? 'Approved' : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '20px',
    backdropFilter: 'blur(10px)'
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700'
  },
  subtitle: {
    margin: '4px 0 0 0',
    color: 'var(--text-muted, #64748b)',
    fontSize: '13px'
  },
  btnTrigger: {
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '10px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)',
    transition: 'transform 0.1s ease'
  },
  formCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)'
  },
  formHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '1px solid var(--border-color, #e2e8f0)'
  },
  formTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600'
  },
  btnCancel: {
    backgroundColor: 'transparent',
    color: 'var(--text-muted, #64748b)',
    border: '1px solid var(--border-color, #e2e8f0)',
    padding: '6px 16px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  formRow: {
    display: 'flex',
    gap: '20px'
  },
  inputGroup: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-muted, #64748b)'
  },
  select: {
    backgroundColor: 'var(--bg-page, #f8fafc)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '14px',
    outline: 'none',
    color: 'var(--text-main, #0f172a)'
  },
  input: {
    backgroundColor: 'var(--bg-page, #f8fafc)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '14px',
    outline: 'none',
    color: 'var(--text-main, #0f172a)'
  },
  formFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '10px',
    paddingTop: '16px',
    borderTop: '1px solid var(--border-color, #e2e8f0)'
  },
  infoText: {
    fontSize: '12px',
    color: 'var(--text-muted, #64748b)'
  },
  btnSave: {
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '10px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  tableCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)'
  },
  tableTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: '600'
  },
  empty: {
    textAlign: 'center',
    padding: '30px',
    color: 'var(--text-muted, #64748b)'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left'
  },
  trHeader: {
    borderBottom: '2px solid var(--border-color, #e2e8f0)'
  },
  th: {
    padding: '12px 16px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '600',
    fontSize: '13px'
  },
  tr: {
    borderBottom: '1px solid var(--border-color, #e2e8f0)'
  },
  tdTitle: {
    padding: '16px',
    fontWeight: '600'
  },
  td: {
    padding: '16px',
    fontSize: '14px'
  },
  badgePending: {
    backgroundColor: '#fef3c7',
    color: '#b45309',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  badgeApproved: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  badgeOffboarded: {
    backgroundColor: '#f1f5f9',
    color: '#64748b',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block',
    border: '1px solid #e2e8f0'
  }
};
